
  import { AccountModule, AccountStore } from '@/store/account';
  import { DashboardModule, DashboardStore } from '@/store/dashboard';
  import { FilterMatchMode } from 'primevue/api';
  import { ref } from 'vue';
  import { Options, Vue } from 'vue-class-component';
  import Chart from 'primevue/chart';
  import { ActiveEntitiesQuery } from '@/apiclient/apiclient_generated';
  import Routes from '@/types/Routes';
  import dateFilter from '@/filters/date-time.filter';

  @Options({
    components: {
      Chart
    }
  })
  export default class Dashboard extends Vue {
    private accountStore: AccountModule = AccountStore;
    private dashboardStore: DashboardModule = DashboardStore;
    private df = dateFilter;

    selectedAccount: any = ref();
    selectedActivation: any = ref();

    customerData = {
      show: true,
      loading: false,
      globalFilter: ['computerName', 'userDomainName', 'userName'],
      filters: ref({
        computerName: { value: [], matchMode: FilterMatchMode.IN },
        userDomainName: { value: [], matchMode: FilterMatchMode.IN },
        userName: { value: [], matchMode: FilterMatchMode.IN },
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
      })
    };

    activeCustomersData = {
      show: true,
      loading: false,
      globalFilter: ['company', 'name', 'surname', 'email'],
      filters: ref({
        company: { value: [], matchMode: FilterMatchMode.IN },
        name: { value: [], matchMode: FilterMatchMode.IN },
        email: { value: [], matchMode: FilterMatchMode.IN },
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
      })
    };

    expiringLicensesData = {
      show: true,
      loading: false,
      globalFilter: ['company', 'name', 'surname', 'email'],
      filters: ref({
        company: { value: [], matchMode: FilterMatchMode.IN },
        name: { value: [], matchMode: FilterMatchMode.IN },
        email: { value: [], matchMode: FilterMatchMode.IN },
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
      })
    };

    latestActivationsData = {
      show: true,
      loading: false,
      globalFilter: ['company', 'accountName', 'accountSurname'],
      filters: ref({
        company: { value: [], matchMode: FilterMatchMode.IN },
        accountName: { value: [], matchMode: FilterMatchMode.IN },
        accountSurname: { value: [], matchMode: FilterMatchMode.IN },
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
      })
    };

    latestValidationsData = {
      show: true,
      loading: false,
      globalFilter: ['company', 'accountName', 'accountSurname'],
      filters: ref({
        company: { value: [], matchMode: FilterMatchMode.IN },
        accountName: { value: [], matchMode: FilterMatchMode.IN },
        accountSurname: { value: [], matchMode: FilterMatchMode.IN },
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
      })
    };

    activeEntitiesData = ref({
      loading: false,
      show: true,

      dateFrom: new Date(),
      dateTo: new Date(),

      labels: [],
      datasets: [
        {
          label: 'Accounts',
          fill: false,
          borderColor: '#1f4f59',
          tension: 0.4,
          data: [],
          hidden: true
        },
        {
          label: 'Licenses',
          fill: false,
          borderColor: '#ffa500',
          tension: 0.4,
          data: [],
          hidden: true
        },
        {
          label: 'Sum Accounts',
          fill: true,
          borderColor: '#011e30',
          tension: 0.4,
          backgroundColor: 'rgba(1,30,48,0.1)',
          data: [],
          hidden: true
        },
        {
          label: 'Sum Licenses',
          fill: false,
          borderColor: '#006400',
          tension: 0.4,
          data: []
        },
        {
          label: 'Sum Activations',
          fill: false,
          borderColor: '#0000FF',
          tension: 0.4,
          data: []
        }
      ]
    });

    basicOptions = ref({
      plugins: {
        legend: {}
      },
      scales: {
        x: {
          ticks: {
            color: '#495057'
          },
          grid: {
            color: '#ebedef'
          }
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: '#495057'
          },
          grid: {
            color: '#ebedef'
          }
        }
      }
    });

    percentage(partialValue, totalValue) {
      return Math.round((100 * partialValue) / totalValue);
    }

    async mounted() {
      this.selectedAccount = [];
      this.selectedActivation = [];

      this.initActiveEntitiesData();

      if (this.isAdminOrEmployee) {
        this.activeCustomersData.loading = true;
        await this.dashboardStore.getLatestCreatedAccounts().finally(() => {
          this.activeCustomersData.loading = false;
        });

        this.expiringLicensesData.loading = true;
        await this.dashboardStore.getExpiringLicenses().finally(() => {
          this.expiringLicensesData.loading = false;
        });

        this.latestActivationsData.loading = true;
        await this.dashboardStore.getLatestActivations().finally(() => {
          this.latestActivationsData.loading = false;
        });

        this.latestValidationsData.loading = true;
        await this.dashboardStore.getLatestValidations().finally(() => {
          this.latestValidationsData.loading = false;
        });

        await this.loadActiveEntitiesData();
      } else {
        this.customerData.loading = true;
        await this.dashboardStore.getCustomerStatistic(this.accountStore.userData.id);
        this.customerData.loading = false;
      }
    }

    get isAdminOrEmployee() {
      return this.accountStore.isAdmin || this.accountStore.isEmployee;
    }

    get customerLicensesStatistic() {
      return this.dashboardStore.customerStatistics.licensesStatistic;
    }

    get customerActivationsStatistic() {
      return this.dashboardStore.customerStatistics.licenseActivationsStatistic;
    }

    latestActivationDblClicked(row) {
      const params = {
        account: row.data.accountId,
        license: row.data.licenseId
      };
      this.$router.push({ path: Routes.management.activations.path, query: params });
    }

    latestAccountDblClicked(row) {
      const accountId = row.data.id;

      this.$router.push({ path: Routes.management.licenses.path, query: { account: accountId } });
    }

    soonExpiringLicensesDblClicked(row) {
      const accountId = row.data.accountId;

      this.$router.push({ path: Routes.management.licenses.path, query: { account: accountId } });
    }

    unmounted() {
      this.dashboardStore.setLatestCreatedAccounts([]);
    }

    getDefaultDate(date = new Date()) {
      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);

      return date;
    }

    initActiveEntitiesData() {
      const entity: any = this.activeEntitiesData;

      const from = new Date();
      from.setMonth(from.getMonth() - 3);

      entity.dateFrom = from;
      entity.dateTo = new Date();

      entity.datasets[0].label = 'Accounts';
      entity.datasets[1].label = 'Licenses';
    }

    async loadActiveEntitiesData() {
      const entity: any = this.activeEntitiesData;

      const query = {
        from: entity.dateFrom.toJSON(),
        to: entity.dateTo.toJSON()
      } as ActiveEntitiesQuery;

      entity.loading = true;
      await this.dashboardStore.getActiveEntities(query);
      entity.loading = false;

      this.setActiveDataResults();
    }

    async loadActiveData(entity, func) {
      const query = {
        from: entity.dateFrom.toJSON(),
        to: entity.dateTo.toJSON()
      } as ActiveEntitiesQuery;

      await func(query);
    }

    setActiveDataResults() {
      const entity: any = this.activeEntitiesData;

      entity.labels = this.activeEntities.dates.map((d) => this.df.format(d));

      entity.datasets[0].data = this.activeEntities.activeAccounts.map((d) => d.count);
      entity.datasets[1].data = this.activeEntities.activeLicenses.map((d) => d.count);
      entity.datasets[2].data = this.activeEntities.accountsAccumulated.map((d) => d.count);
      entity.datasets[3].data = this.activeEntities.licensesAccumulated.map((d) => d.count);
      entity.datasets[4].data = this.activeEntities.activationsAccumulated.map((d) => d.count);
    }

    get latestAccounts() {
      return this.dashboardStore.latestCreatedAccounts;
    }

    get expiringLicenses() {
      return this.dashboardStore.expiringLicenses;
    }

    get latestActivations() {
      return this.dashboardStore.latestActivations;
    }

    get latestValidations() {
      return this.dashboardStore.latestValidations;
    }

    get activeEntities() {
      return this.dashboardStore.activeEntities;
    }

    rowClassLatestAccounts(data) {
      var classes = 'cursor-pointer';

      classes += data.emailConfirmed ? '' : ' row-not-mail-validated';

      var diff = new Date(data.licenseExpiryDate).getTime() - new Date().getTime();
      var diffDays = diff / (1000 * 3600 * 24);

      if (diffDays < 7) {
        classes += ' row-expiry-1-days';
      } else if (diffDays < 14) {
        classes += ' row-expiry-2-days';
      }

      return classes;
    }

    mailValidatedClass(data) {
      return [
        {
          'mail-validated': data.emailConfirmed == true,
          'mail-not-validated': data.emailConfirmed == false
        }
      ];
    }

    hasActivationsClass(data) {
      return data.activationsCount > 0 ? 'hasActivations' : 'hasNoActivations';
    }

    expiryDateClass(data) {
      var diff = new Date(data.licenseExpiryDate).getTime() - new Date().getTime();
      var diffDays = diff / (1000 * 3600 * 24);

      if (diffDays < 7) {
        return 'expiry-1-days';
      } else if (diffDays < 14) {
        return 'expiry-2-days';
      }
    }

    expiryDateExpiringSoonClass(data) {
      var diff = new Date(data.expiryDate).getTime() - new Date().getTime();
      var diffDays = diff / (1000 * 3600 * 24);

      if (diffDays < 7) {
        return 'expiry-1-days';
      } else if (diffDays < 14) {
        return 'expiry-2-days';
      }
    }

    rowClass() {
      return 'cursor-pointer';
    }
  }
